import { TimePicker } from 'components/forms/TimePicker'
import { setMinutes, setHours, setSeconds, setMilliseconds } from 'date-fns'
import { useFitterRelativeTimeInfo } from 'components/schedule/useFitterRelativeTimeInfo'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AppointmentFormData, AvailabilitiesResponse, RecommendationResource } from 'types'
import { determineResourceIds } from '../../../../../util/determineResourceIds'
import { useDateFormat } from '../../../../../hooks'

interface Props {
  resource: Pick<RecommendationResource, 'id' | 'timezone'>
  durationInMinutes: number
  availabilityData?: AvailabilitiesResponse
}

const ManualMode: React.FC<Props> = ({ resource, durationInMinutes, availabilityData }) => {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext<AppointmentFormData>()
  const [manualStartTime, setManualStartTime] = useState<Date | null>(null)
  const [clientStartDate, timezone] = watch(['clientStartDate', 'customer_timezone']) as [Date, string]
  const { dateToISODateKey } = useDateFormat()
  const dayAvailability = availabilityData?.dayAvailability

  const onChange = useCallback(
    (value: Date | null) => {
      if (!value) {
        return
      }

      const startTimeWithHourMinutes = zonedTimeToUtc(
        setMilliseconds(setSeconds(setMinutes(setHours(clientStartDate, value.getHours()), value.getMinutes()), 0), 0),
        timezone
      )

      setManualStartTime(startTimeWithHourMinutes)
      setValue('clientStartDate', startTimeWithHourMinutes)
      setValue('manualStartTime', startTimeWithHourMinutes)
      setValue('duration_in_minutes', durationInMinutes)
      setValue('duration_in_minutes_temp', durationInMinutes)
      setValue('specialist_id', resource.id)
      setValue('start_date', startTimeWithHourMinutes)

      // Determine resource_ids based on specialist availability and cabin availability
      const resourceIds = determineResourceIds({
        specialistId: resource.id,
        dayAvailability,
        clientStartDate,
        dateToISODateKey,
      })
      if (resourceIds) {
        setValue('resource_ids', resourceIds)
      }
    },
    [setValue, clientStartDate, timezone, durationInMinutes, resource.id, dayAvailability, dateToISODateKey]
  )

  const areInSameTimeZone = timezone === resource.timezone
  const fitterRelativeTimeInfo = useFitterRelativeTimeInfo(manualStartTime || clientStartDate, timezone, resource.timezone)
  const { fitterTime, isDayAfter, isDayBefore } = fitterRelativeTimeInfo()
  const fitterTimeText =
    areInSameTimeZone || !manualStartTime
      ? ''
      : t('common.label.timeFitterDiff', {
          fitterTime,
          dayDiff: isDayBefore ? t('common.label.timeFitterDiffMinus1') : isDayAfter ? t('common.label.timeFitterDiffPlus1') : '',
        })

  return <TimePicker onChange={onChange} label={t('common.label.customerTime')} name={'manualStartTime'} helperText={fitterTimeText} />
}

export { ManualMode }
